import { useCallback, useMemo, useState } from "react";
import { useClientAds } from "api/Client/ads";

const STORAGE_KEY = "client_seen_notification_ids";

const readSeenIds = () => {
  try {
    const storedValue = localStorage.getItem(STORAGE_KEY);
    const parsedValue = storedValue ? JSON.parse(storedValue) : [];
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
};

const writeSeenIds = (ids) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
};

const getNotificationId = (item, index) =>
  String(item?.id || item?.title || item?.created_at || `notification-${index}`);

export default function useClientNotifications() {
  const adsQuery = useClientAds();
  const [seenIds, setSeenIds] = useState(readSeenIds);

  const notifications = useMemo(() => {
    const ads = adsQuery?.data?.data || [];

    return ads
      .map((item, index) => ({
        ...item,
        notificationId: getNotificationId(item, index),
      }))
      .sort((firstItem, secondItem) => {
        const firstDate = new Date(
          firstItem.created_at || firstItem.createdAt || firstItem.date || 0,
        ).getTime();
        const secondDate = new Date(
          secondItem.created_at || secondItem.createdAt || secondItem.date || 0,
        ).getTime();

        if (firstDate !== secondDate) {
          return secondDate - firstDate;
        }

        return Number(secondItem.id || 0) - Number(firstItem.id || 0);
      });
  }, [adsQuery?.data?.data]);

  const unreadNotifications = useMemo(
    () =>
      notifications.filter(
        (notification) => !seenIds.includes(notification.notificationId),
      ),
    [notifications, seenIds],
  );

  const markAllAsRead = useCallback(() => {
    if (!notifications.length) return;

    const nextSeenIds = notifications.map((notification) => notification.notificationId);
    writeSeenIds(nextSeenIds);
    setSeenIds(nextSeenIds);
  }, [notifications]);

  return {
    ...adsQuery,
    notifications,
    unreadNotifications,
    unreadCount: unreadNotifications.length,
    hasUnreadNotifications: unreadNotifications.length > 0,
    markAllAsRead,
  };
}
